import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  TextField,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { FormTextField, FormSelect } from '@personal-dashboard/ui';
import dayjs, { Dayjs } from 'dayjs';

export interface EventFormData {
  title: string;
  description: string;
  startTime: Dayjs;
  endTime: Dayjs;
  location: string;
  color: string;
  repeat: 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';
}

interface EventDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: EventFormData) => void;
  onDelete?: () => void;
  initialData?: Partial<EventFormData>;
  title: string;
}

const EVENT_COLORS = [
  { value: '#1976d2', label: 'Blue' },
  { value: '#4caf50', label: 'Green' },
  { value: '#f44336', label: 'Red' },
  { value: '#ff9800', label: 'Orange' },
  { value: '#9c27b0', label: 'Purple' },
  { value: '#607d8b', label: 'Grey' },
];

const REPEAT_OPTIONS = [
  { value: 'never', label: 'Never' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

const EventDialog: React.FC<EventDialogProps> = ({ open, onClose, onSubmit, onDelete, initialData, title }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const methods = useForm<EventFormData>({
    defaultValues: {
      title: initialData?.title || '',
      description: initialData?.description || '',
      startTime: initialData?.startTime || dayjs(),
      endTime: initialData?.endTime || dayjs().add(1, 'hour'),
      location: initialData?.location || '',
      color: initialData?.color || theme.palette.primary.main,
      repeat: initialData?.repeat || 'never',
    }
  });

  const { control, handleSubmit, reset } = methods;

  React.useEffect(() => {
    if (open) {
      reset({
        title: initialData?.title || '',
        description: initialData?.description || '',
        startTime: initialData?.startTime || dayjs(),
        endTime: initialData?.endTime || dayjs().add(1, 'hour'),
        location: initialData?.location || '',
        color: initialData?.color || theme.palette.primary.main,
        repeat: initialData?.repeat || 'never',
      });
    }
  }, [open, initialData, reset, theme.palette.primary.main]);

  const onFormSubmit = (data: EventFormData) => {
    onSubmit(data);
    onClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>{title}</DialogTitle>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onFormSubmit)}>
            <DialogContent>
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Controller
                  name="title"
                  control={control}
                  rules={{ required: 'Title is required' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Event Title"
                      fullWidth
                      autoFocus
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="startTime"
                      control={control}
                      render={({ field }) => (
                        <DateTimePicker
                          {...field}
                          label="Start Time"
                          slotProps={{ textField: { fullWidth: true } }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="endTime"
                      control={control}
                      render={({ field }) => (
                        <DateTimePicker
                          {...field}
                          label="End Time"
                          slotProps={{ textField: { fullWidth: true } }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormTextField
                      name="location"
                      label="Location"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormSelect
                      name="repeat"
                      label="Repeat"
                      options={REPEAT_OPTIONS}
                    />
                  </Grid>
                </Grid>

                <FormTextField
                  name="description"
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                />

                <FormSelect
                  name="color"
                  label="Label Color"
                  options={EVENT_COLORS}
                />
              </Box>
            </DialogContent>
            <DialogActions
              sx={{
                p: 2,
                pt: 0,
                justifyContent: 'space-between',
                ...(isMobile
                  ? { borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }
                  : {}),
              }}
            >
              <Box>
                {onDelete && (
                  <Button color="error" size={isMobile ? 'medium' : 'small'}>Delete</Button>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button onClick={onClose} size={isMobile ? 'large' : 'small'}>Cancel</Button>
                <Button
                  variant="contained"
                  type="submit"
                  size={isMobile ? 'large' : 'small'}
                  sx={isMobile ? { minWidth: 140, minHeight: 48 } : undefined}
                >
                  Save Event
                </Button>
              </Box>
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>
    </LocalizationProvider>
  );
};

export default EventDialog;
